import React, { useEffect, useState, useMemo } from "react";
import { Trash2, Edit, Search, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import Apiurl from "../environment/Apiurl";

function Doctors() {
  const [responseData, setResponseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [userPrivileges, setUserPrivileges] = useState({});
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingDoctorId, setEditingDoctorId] = useState(null);
  const [generateDoctorId, setGenerateDoctorId] = useState("");
  const [departmentsData, setDepartmentsData] = useState([]);

  // Fetch generated user ID
  const fetchGenerate = async () => {
    try {
      const response = await axios.get(`${Apiurl}/generate_doctor_id`);
      console.log("Fetched doctor ID:", response.data);
      if (response.data && response.data.doctor_id) {
        const doctorId = response.data.doctor_id;
        setGenerateDoctorId(doctorId);
        setAddFormData((prev) => ({ ...prev, doctor_id: doctorId }));
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error(
        "Error generating doctor ID:",
        error.message,
        error.response?.data
      );
      setError("Failed to generate doctor ID. Using temporary ID...");
      setGenerateDoctorId("TEMP-D-001");
      setAddFormData((prev) => ({ ...prev, doctor_id: "TEMP-D-001" }));
      setTimeout(() => setError(null), 3000);
    }
  };

  // Form state for adding a new doctor
  const [addFormData, setAddFormData] = useState({
    doctor_id: "",
    doctor_name: "",
    department: "",
    speciality: "",
    mobile_no: "",
    email: "",
    qualification: "",
    status: "Active",
  });

  // Form state for updating a doctor
  const [updateFormData, setUpdateFormData] = useState({
    doctor_id: "",
    doctor_name: "",
    department: "",
    speciality: "",
    mobile_no: "",
    qualification: "",
    email: "",
    status: "Active",
  });

  // Fetch user privileges based on role_id
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRoleId = localStorage.getItem("role_id") || "unknown";
      console.log("Fetching privileges for role_id:", userRoleId);

      if (userRoleId === "unknown") {
        console.warn("No role_id found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log("Fetched roles data:", response.data);

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error("Invalid or empty roles data received");
      }

      const roleData = response.data.find(
        (role) => role.role_id === userRoleId
      );

      if (roleData) {
        console.log("Found role data for role_id:", userRoleId, roleData);
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
        });
        console.log("Set user privileges:", privileges);
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_id:", userRoleId);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response?.data
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Doctors page
  const hasPermission = (permission) => {
    const doctorsPrivileges = userPrivileges["Doctors"];
    if (!doctorsPrivileges) {
      console.log("No privileges found for Doctors page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Doctors:`,
      doctorsPrivileges
    );
    if (doctorsPrivileges.all) return true;
    const hasPerm = doctorsPrivileges[permission] || false;
    console.log(`Permission '${permission}' result:`, hasPerm);
    return hasPerm;
  };

  // Fetch doctors data
  const fetchDoctorData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Doctors page");
      setError("You do not have permission to view doctors.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      const userType = localStorage.getItem("user_type") || "unknown";
      const username = localStorage.getItem("username") || "";
      let doctors = [];
      if (userType === "Doctor") {
        if (!username) {
          throw new Error("Doctor username not found in localStorage");
        }
        // Fetch doctor by username (doctor_id)
        const response = await axios.get(
          `${Apiurl}/doctor_by_doctor_id/${username}`
        );
        console.log(
          "Fetched doctor data for username:",
          username,
          response.data
        );
        doctors = response.data ? [response.data] : [];
      } else {
        // For non-doctors, fetch all doctors
        const response = await axios.get(`${Apiurl}/doctor`);
        console.log("Fetched all doctors data:", response.data);
        doctors = response.data || [];
      }
      setResponseData(doctors);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching doctors:",
        error.message,
        error.response?.data
      );
      setError("Failed to fetch doctors. Please try again.");
      setResponseData([]);
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Fetch department data
  const fetchdepartmentdata = async () => {
    try {
      const response = await axios.get(`${Apiurl}/department`);
      console.log("Fetched departments data:", response.data);
      setDepartmentsData(response.data || []);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching departments:",
        error.message,
        error.response?.data
      );
      setError("Failed to fetch departments. Please try again.");
      setDepartmentsData([]);
      setTimeout(() => setError(null), 3000);
    }
  };

  // Get unique specialities from departments
  const getSpecialities = () => {
    const specialities = new Set();
    departmentsData.forEach((dept) => {
      if (Array.isArray(dept.speciality)) {
        dept.speciality.forEach((spec) => specialities.add(spec));
      } else if (dept.speciality) {
        specialities.add(dept.speciality);
      }
    });
    return Array.from(specialities);
  };

  // Get specialities for a specific department
  const getSpecialitiesForDepartment = (department) => {
    if (!department) return [];
    const dept = departmentsData.find((d) => d.department === department);
    if (!dept) return [];
    return Array.isArray(dept.speciality) ? dept.speciality : [dept.speciality];
  };

  // Validate form data
  const validateForm = (data) => {
    if (!data.doctor_id) return "Doctor ID is required.";
    if (data.doctor_id.length > 20)
      return "Doctor ID must be 20 characters or less.";
    if (!data.doctor_name) return "Doctor name is required.";
    if (data.doctor_name.length > 100)
      return "Doctor name must be 100 characters or less.";
    if (!data.department) return "Department is required.";
    if (!data.speciality) return "Speciality is required.";
    if (data.speciality.length > 100)
      return "Speciality must be 100 characters or less.";
    if (!data.mobile_no) return "Mobile number is required.";
    if (!/^[0-9]{10}$/.test(data.mobile_no))
      return "Mobile number must be a valid 10-digit number.";
    if (!data.email) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email))
      return "Invalid email format.";
    if (!data.status) return "Status is required.";
    return null;
  };

  // Handle add doctor submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Doctors page");
      setError("You do not have permission to add doctors.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(addFormData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Adding doctor with data:", addFormData);
      await axios.post(`${Apiurl}/doctor`, addFormData);
      fetchDoctorData();
      closeAddModal();
    } catch (error) {
      console.error(
        "Error adding doctor:",
        error.message,
        error.response?.data
      );
      setError("Failed to add doctor. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle update doctor submission
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Doctors page");
      setError("You do not have permission to update doctors.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!editingDoctorId) {
      console.warn("Invalid doctor ID:", editingDoctorId);
      setError("Invalid doctor ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(updateFormData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log(
        "Updating doctor with ID:",
        editingDoctorId,
        "Data:",
        updateFormData
      );
      await axios.put(`${Apiurl}/doctor/${editingDoctorId}`, updateFormData);
      fetchDoctorData();
      closeUpdateModal();
    } catch (error) {
      console.error(
        "Error updating doctor:",
        error.message,
        error.response?.data
      );
      setError("Failed to update doctor. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete doctor
  const deleteDoctor = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Doctors page");
      setError("You do not have permission to delete doctors.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid doctor ID:", deleteId);
      setError("Invalid doctor ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Deleting doctor with ID:", deleteId);
      await axios.delete(`${Apiurl}/doctor/${deleteId}`);
      fetchDoctorData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting doctor:",
        error.message,
        error.response?.data
      );
      setError("Failed to delete doctor. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
  // Handle edit button click
  const handleEdit = (doctor) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Doctors page");
      setError("You do not have permission to edit doctors.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!doctor._id) {
      console.warn("Invalid doctor ID:", doctor._id);
      setError("Invalid doctor ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Opening Edit Doctor modal for ID:",
      doctor._id,
      "Doctor:",
      doctor
    );
    setEditingDoctorId(doctor._id);
    setUpdateFormData({
      doctor_id: doctor.doctor_id || "",
      doctor_name: doctor.doctor_name || "",
      department: doctor.department || "",
      speciality: doctor.speciality || "",
      mobile_no: doctor.mobile_no || "",
      qualification: doctor.qualification || "",
      email: doctor.email || "",
      status: doctor.status || "Active",
    });
    setShowUpdateModal(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Doctors page");
      setError("You do not have permission to delete doctors.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid doctor ID:", id);
      setError("Invalid doctor ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Doctor modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Doctor modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Open add modal
  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Doctors page");
      setError("You do not have permission to add doctors.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Add Doctor modal");
    fetchGenerate();
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    console.log("Closing Add Doctor modal");
    setShowAddModal(false);
    setAddFormData({
      doctor_id: "",
      doctor_name: "",
      department: "",
      speciality: "",
      mobile_no: "",
      qualification: "",
      email: "",
      status: "Active",
    });
    setGenerateDoctorId("");
    setError(null);
  };

  // Close update modal
  const closeUpdateModal = () => {
    console.log("Closing Update Doctor modal");
    setShowUpdateModal(false);
    setEditingDoctorId(null);
    setUpdateFormData({
      doctor_id: "",
      doctor_name: "",
      department: "",
      speciality: "",
      mobile_no: "",
      qualification: "",
      email: "",
      status: "Active",
    });
    setError(null);
  };

  // Handle input changes for add form
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Add form input changed: ${name} = ${value}`);
    setAddFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle input changes for update form
  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    console.log(`Update form input changed: ${name} = ${value}`);
    setUpdateFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Fetch initial data
  useEffect(() => {
    console.log("Fetching initial data...");
    fetchUserPrivileges();
    fetchdepartmentdata();
  }, []);

  useEffect(() => {
    console.log(
      "Checking read permission after privileges loaded:",
      userPrivileges
    );
    if (hasPermission("read")) {
      console.log("Fetching doctors data due to read permission");
      fetchDoctorData();
    } else if (!loadingPrivileges) {
      console.warn("Read permission denied for Doctors page");
      setError("You do not have permission to view doctors.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Reset current page on search or items per page change
  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm or itemsPerPage change"
    );
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Filter data with useMemo
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.doctor_id,
        item.doctor_name,
        item.department,
        item.speciality,
        item.email,
        item.mobile_no,
        item.qualification,
        item.status,
      ].some((field) => field?.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    console.log("Filtered data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  if (loadingPrivileges) {
    return <div className="text-center py-5">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_id:",
      localStorage.getItem("role_id"),
      "on Doctors page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom { 
          background-color: #01AEBE; 
          color: white; 
          border: none;
        }
        .btn-custom:hover { 
          background-color: #018A97; 
          color: white;
        }
        .badge-custom-active { 
          background-color: #E6F4EA; 
          color: #17826B; 
          font-weight: 600; 
          border-radius: 20px; 
          padding: 4px 12px; 
          font-size: .85rem; 
        }
        .badge-custom-inactive { 
          background-color: #FFE6E6; 
          color: #D32F2F; 
          font-weight: 600; 
          border-radius: 20px; 
          padding: 4px 12px; 
          font-size: .85rem; 
        }
      `}</style>

      {hasPermission("create") && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <button className="btn btn-custom btn-sm" onClick={openAddModal}>
            <i className="fas fa-plus me-2"></i> Add Doctor
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log("Changing items per page to:", e.target.value);
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="All">Show All</option>
                  {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "35%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Doctor ID</th>
                  <th>Doctor Name</th>
                  <th>Department</th>
                  <th>Speciality</th>
                  <th>Mobile No</th>
                  <th>Email</th>
                  <th>Status</th>
                  {(hasPermission("update") || hasPermission("delete")) && (
                    <th>Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((doctor, idx) => (
                    <tr key={doctor._id || idx} className="align-middle">
                      <td>{indexOfFirstItem + idx + 1}</td>
                      <td>{doctor.doctor_id || "-"}</td>
                      <td>{doctor.doctor_name || "-"}</td>
                      <td>{doctor.department || "-"}</td>
                      <td>{doctor.speciality || "-"}</td>
                      <td>{doctor.mobile_no || "-"}</td>
                      <td>{doctor.email || "-"}</td>
                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center ${
                            doctor.status?.toLowerCase() === "active"
                              ? "badge-custom-active"
                              : "badge-custom-inactive"
                          }`}
                        >
                          {doctor.status?.toLowerCase() === "active" ? (
                            <CheckCircle size={14} className="me-2" />
                          ) : (
                            <XCircle size={14} className="me-2" />
                          )}
                          {doctor.status?.toLowerCase() === "active"
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </td>
                      {(hasPermission("update") || hasPermission("delete")) && (
                        <td>
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              style={{ backgroundColor: "transparent" }}
                              onClick={() => openDeleteModal(doctor._id)}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              style={{ backgroundColor: "transparent" }}
                              onClick={() => handleEdit(doctor)}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        hasPermission("update") || hasPermission("delete")
                          ? 9
                          : 8
                      }
                      className="text-center py-4"
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <div className="text-muted">
                          <i className="fas fa-search fa-2x mb-2"></i>
                          <br />
                          No doctors found matching your search criteria
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="container">
            <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() =>
                  currentPage > 1 && handlePageClick(currentPage - 1)
                }
                disabled={currentPage === 1}
              >
                <i className="fas fa-chevron-left"></i>
                <span className="d-none d-sm-inline">Previous</span>
              </button>

              <div
                className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                style={{ maxWidth: "100%" }}
              >
                {pages.map((page, index) => (
                  <button
                    key={index}
                    className={`btn btn-sm px-3 flex-shrink-0 ${
                      page === currentPage
                        ? "text-white"
                        : "btn-outline-secondary"
                    }`}
                    style={
                      page === currentPage
                        ? { backgroundColor: "#20c997", border: "none" }
                        : {}
                    }
                    onClick={() => handlePageClick(page)}
                    disabled={page === "..."}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary d-flex align-items-center gap-2"
                onClick={() =>
                  currentPage < totalPages && handlePageClick(currentPage + 1)
                }
                disabled={currentPage === totalPages}
              >
                <span className="d-none d-sm-inline">Next</span>
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Doctor Modal */}
      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addDoctorModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h6 className="modal-title text-dark" id="addDoctorModalLabel">
                  Add Doctor
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleAddSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="add_doctor_id" className="form-label">
                        Doctor ID
                      </label>
                      <input
                        type="text"
                        placeholder="Enter doctor ID"
                        className="form-control"
                        value={generateDoctorId}
                        required
                        readOnly
                      />
                    </div>
                    <div className="col-md-6 mb-1">
                      <label htmlFor="add_doctor_name" className="form-label">
                        Doctor Name
                      </label>
                      <input
                        id="add_doctor_name"
                        name="doctor_name"
                        type="text"
                        placeholder="Enter doctor name"
                        className="form-control"
                        value={addFormData.doctor_name}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="add_mobile_no" className="form-label">
                        Mobile No
                      </label>
                      <input
                        id="add_mobile_no"
                        name="mobile_no"
                        type="text"
                        placeholder="Enter mobile number"
                        pattern="[0-9]{10}"
                        title="Please enter a valid 10-digit mobile number"
                        className="form-control"
                        value={addFormData.mobile_no}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-1">
                      <label htmlFor="add_email" className="form-label">
                        Email
                      </label>
                      <input
                        id="add_email"
                        name="email"
                        type="email"
                        placeholder="Enter email address"
                        className="form-control"
                        value={addFormData.email}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="add_qualification" className="form-label">
                        Qualification
                      </label>
                      <input
                        id="add_qualification"
                        name="qualification"
                        type="text"
                        placeholder="Enter qualification"
                        className="form-control"
                        value={addFormData.qualification}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-1">
                      <label htmlFor="add_department" className="form-label">
                        Department
                      </label>
                      <select
                        id="add_department"
                        name="department"
                        className="form-select"
                        value={addFormData.department}
                        onChange={(e) => {
                          handleAddInputChange(e);
                          setAddFormData((prev) => ({
                            ...prev,
                            department: e.target.value,
                            speciality: "",
                          }));
                        }}
                        required
                      >
                        <option value="">Select Department</option>
                        {departmentsData.map((dept) => (
                          <option key={dept._id} value={dept.department}>
                            {dept.department}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="add_speciality" className="form-label">
                        Speciality
                      </label>
                      <input
                        id="add_speciality"
                        name="speciality"
                        type="text"
                        placeholder="Enter Speciality"
                        className="form-control"
                        value={addFormData.speciality}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                    <div className="col-lg-6">
                      <label htmlFor="add_status" className="form-label">
                        Status
                      </label>
                      <select
                        id="add_status"
                        name="status"
                        className="form-select"
                        value={addFormData.status}
                        onChange={handleAddInputChange}
                        required
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="text-center mb-2">
                  <button
                    type="button"
                    className="btn btn-sm px-3 me-2"
                    style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                    onClick={closeAddModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-custom btn-sm px-4"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Update Doctor Modal */}
      {hasPermission("update") && (
        <div
          className={`modal fade ${showUpdateModal ? "show" : ""}`}
          style={{ display: showUpdateModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="updateDoctorModalLabel"
          aria-hidden={!showUpdateModal}
        >
          <div className="modal-dialog modal-md modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h6
                  className="modal-title text-dark"
                  id="updateDoctorModalLabel"
                >
                  Update Doctor
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeUpdateModal}
                  aria-label="Close"
                ></button>
              </div>
              <form onSubmit={handleUpdateSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="update_doctor_id" className="form-label">
                        Doctor ID
                      </label>
                      <input
                        id="update_doctor_id"
                        name="doctor_id"
                        type="text"
                        placeholder="Enter doctor ID"
                        className="form-control"
                        value={updateFormData.doctor_id}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-1">
                      <label
                        htmlFor="update_doctor_name"
                        className="form-label"
                      >
                        Doctor Name
                      </label>
                      <input
                        id="update_doctor_name"
                        name="doctor_name"
                        type="text"
                        placeholder="Enter doctor name"
                        className="form-control"
                        value={updateFormData.doctor_name}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="update_mobile_no" className="form-label">
                        Mobile No
                      </label>
                      <input
                        id="update_mobile_no"
                        name="mobile_no"
                        type="text"
                        placeholder="Enter mobile number"
                        pattern="[0-9]{10}"
                        title="Please enter a valid 10-digit mobile number"
                        className="form-control"
                        value={updateFormData.mobile_no}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-1">
                      <label htmlFor="update_email" className="form-label">
                        Email
                      </label>
                      <input
                        id="update_email"
                        name="email"
                        type="email"
                        placeholder="Enter email address"
                        className="form-control"
                        value={updateFormData.email}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label
                        htmlFor="update_qualification"
                        className="form-label"
                      >
                        Qualification
                      </label>
                      <input
                        id="update_qualification"
                        name="qualification"
                        type="text"
                        placeholder="Enter qualification"
                        className="form-control"
                        value={updateFormData.qualification}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                    <div className="col-lg-6 mb-1">
                      <label htmlFor="update_department" className="form-label">
                        Department
                      </label>
                      <select
                        id="update_department"
                        name="department"
                        className="form-select"
                        value={updateFormData.department}
                        onChange={(e) => {
                          handleUpdateInputChange(e);
                          setUpdateFormData((prev) => ({
                            ...prev,
                            department: e.target.value,
                            speciality: "",
                          }));
                        }}
                        required
                      >
                        <option value="">Select Department</option>
                        {departmentsData.map((dept) => (
                          <option key={dept._id} value={dept.department}>
                            {dept.department}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-1">
                      <label htmlFor="update_speciality" className="form-label">
                        Speciality
                      </label>
                      <input
                        id="update_speciality"
                        name="speciality"
                        type="text"
                        placeholder="Enter Speciality"
                        className="form-control"
                        value={updateFormData.speciality}
                        onChange={handleUpdateInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-1">
                      <label htmlFor="update_status" className="form-label">
                        Status
                      </label>
                      <select
                        id="update_status"
                        name="status"
                        className="form-select"
                        value={updateFormData.status}
                        onChange={handleUpdateInputChange}
                        required
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="text-center mb-2">
                  <button
                    type="button"
                    className="btn btn-sm px-3 me-2"
                    style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                    onClick={closeUpdateModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-custom btn-sm px-4"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteDoctorModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: "#dc2626", fontSize: "20px" }}
                    ></i>
                  </span>
                </div>
                <h5 className="modal-title" id="deleteDoctorModalLabel">
                  Delete Doctor
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this doctor?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={deleteDoctor}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || showUpdateModal || showDeleteModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </div>
  );
}

export default Doctors;
